"use client";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BarChart3, Loader2, Search } from "lucide-react";
import { useState } from "react";
import { usePeriodeStore } from "@/hooks/use-periode";
import { useMasterPeriode } from "@/hooks/use-master-periode";
import { apiClient } from "@/lib/api-client";

// --- Interface ---
interface MasterPeriodeGrade {
  master_periode_grade_id: number;
  master_periode_id: number;
  grade: number;
  nilai_min: number;
  nilai_max: number;
}

interface MasterPeriodeGradeResponse {
  datas: MasterPeriodeGrade[];
}

// --- API FUNCTION ---
const fetchPeriodeGradeAPI = async (
  id: number,
): Promise<MasterPeriodeGrade[]> => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  const result = await apiClient<MasterPeriodeGradeResponse>(
    `${baseUrl}/api/v1/master-periode-grade/${id}`,
    {
      headers: {
        Accept: "application/json",
      },
    },
  );

  return result.datas;
};

export default function PeriodeGradePage() {
  const [searchQuery, setSearchQuery] = useState("");

  // --- PERIOD SYNC ---
  const { activePeriod } = usePeriodeStore();
  const { data: masterPeriodes } = useMasterPeriode();

  const currentPeriodObj = masterPeriodes?.find(
    (p) => `${p.tahun}-${p.semester}` === activePeriod,
  );
  const master_periode_id = currentPeriodObj?.master_periode_id;

  // --- REACT QUERY: FETCH DATA ---
  const {
    data: periodeGrades,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["periode-grades", master_periode_id],
    queryFn: () => fetchPeriodeGradeAPI(master_periode_id!),
    enabled: !!master_periode_id,
  });

  // --- Filter Logic ---
  const filteredData = (periodeGrades || []).filter(
    (item) =>
      item.grade.toString().includes(searchQuery) ||
      item.nilai_min.toString().includes(searchQuery) ||
      item.nilai_max.toString().includes(searchQuery),
  );

  const getGradeColor = (grade: number) => {
    if (grade <= 2) return "bg-slate-100 text-slate-700 border-slate-200";
    if (grade <= 4) return "bg-blue-50 text-blue-700 border-blue-200";
    if (grade <= 6) return "bg-indigo-50 text-indigo-700 border-indigo-200";
    return "bg-violet-100 text-violet-800 border-violet-300 shadow-sm font-bold";
  };

  return (
    <div>
      <Card className="shadow-sm border-slate-200 overflow-hidden">
        <CardHeader className="bg-slate-50/80 border-b border-slate-100 pb-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1.5 flex flex-row items-center gap-3">
              <div className="p-2.5 bg-blue-100/50 rounded-lg border border-blue-200/50 hidden sm:block">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
                  Konfigurasi Grade Periode
                </CardTitle>
                <CardDescription className="text-slate-500 text-sm mt-1">
                  Atur rentang nilai (min/max) untuk setiap tingkatan grade
                  remunerasi.
                </CardDescription>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cari grade atau nilai..."
                  className="pl-9 bg-white border-slate-200 focus-visible:ring-blue-500 shadow-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <Badge
                variant="secondary"
                className="w-full sm:w-auto justify-center bg-blue-50/80 text-blue-700 hover:bg-blue-50/80 border-blue-200 shadow-sm px-4 py-2 text-sm font-semibold whitespace-nowrap"
              >
                Periode Aktif:{" "}
                {currentPeriodObj
                  ? `${currentPeriodObj.semester === 1 ? "Ganjil" : "Genap"} ${currentPeriodObj.tahun}/${parseInt(currentPeriodObj.tahun) + 1}`
                  : "..."}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-white">
                <TableRow className="border-b-slate-200 hover:bg-transparent">
                  <TableHead className="w-32 pl-6 text-slate-600 font-semibold text-center">
                    Tingkat Grade
                  </TableHead>
                  <TableHead className="text-slate-600 font-semibold text-center">
                    Rentang Nilai (Min - Max)
                  </TableHead>
                  <TableHead className="w-32 text-right pr-6 text-slate-600 font-semibold">
                    Aksi
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400 space-y-3">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                        <p className="text-sm">Memuat data grade...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {isError && (
                  <TableRow>
                    <TableCell colSpan={3} className="h-64 text-center">
                      <div className="text-red-500 font-medium text-sm">
                        Gagal memuat data dari server.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && filteredData.length > 0
                  ? filteredData.map((item) => (
                      <TableRow
                        key={item.master_periode_grade_id}
                        className="hover:bg-slate-50 transition-colors group"
                      >
                        <TableCell className="pl-6 align-middle py-4 text-center">
                          <Badge
                            variant="outline"
                            className={`text-sm px-3 py-1 rounded-md ${getGradeColor(item.grade)}`}
                          >
                            Grade {item.grade}
                          </Badge>
                        </TableCell>

                        <TableCell className="align-middle py-4">
                          <div className="flex items-center justify-center gap-3">
                            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-1.5 min-w-20 text-center">
                              <span className="text-xs text-slate-400 block mb-0.5 uppercase tracking-wider font-semibold">
                                Min
                              </span>
                              <span className="font-semibold tabular-nums text-slate-700">
                                {item.nilai_min}
                              </span>
                            </div>

                            <ArrowRight className="w-4 h-4 text-slate-300" />

                            <div className="bg-blue-50/50 border border-blue-100 rounded-md px-4 py-1.5 min-w-20 text-center">
                              <span className="text-xs text-blue-400/80 block mb-0.5 uppercase tracking-wider font-semibold">
                                Max
                              </span>
                              <span className="font-semibold tabular-nums text-blue-700">
                                {item.nilai_max}
                              </span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="text-right pr-6 align-middle py-4">
                          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors opacity-0 group-hover:opacity-100">
                            Edit Range
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  : !isLoading &&
                    !isError && (
                      <TableRow>
                        <TableCell colSpan={3} className="h-48 text-center">
                          <div className="flex flex-col items-center justify-center text-slate-500 space-y-3">
                            <div className="bg-slate-50 p-4 rounded-full border border-slate-100">
                              <BarChart3 className="w-8 h-8 text-slate-300" />
                            </div>
                            <p className="text-sm text-slate-500">
                              Tidak ada data grade yang cocok dengan <br />
                              <span className="font-semibold text-slate-700">
                                "{searchQuery}"
                              </span>
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
